/**
 * NotificationsBell.jsx — header bell that shows unread in-app notification
 * count and a small dropdown of recent items.
 *
 * Polls /api/notifications every 60s while the tab is open. Marks items
 * read on click. Designed to live in AppShell next to HeaderCoinBadge.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Coins, Heart, Loader2, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60000;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const iconFor = (type) => {
  if (type === 'thank_you') return <Heart className="w-4 h-4 text-pink-300" />;
  if (type === 'coin_gift') return <Coins className="w-4 h-4 text-amber-300" />;
  return <Bell className="w-4 h-4 text-slate-300" />;
};

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [messagingUnread, setMessagingUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/notifications?limit=20`);
      if (!r.ok) return;
      const d = await r.json();
      setItems(d.notifications || []);
      setUnread(Number(d.unread_count || 0));
    } finally {
      setLoading(false);
    }
  }, []);

  // Lightweight unread-only poll (cheap query) when the dropdown is closed.
  // Also fires a celebratory toast for the first unseen coin_gift each
  // session — uses localStorage to dedupe across reloads.
  // Feb 26 2026: now also folds in the messaging unread total (DMs +
  // broadcasts) so a single bell surfaces everything that needs the
  // user's attention. The dropdown still lists the legacy notifications
  // only — tapping the "Inbox" CTA in the dropdown jumps to /inbox.
  const fetchCount = useCallback(async () => {
    try {
      const [nr, mr] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/notifications/unread-count`).catch(() => null),
        authFetch(`${BACKEND_URL}/api/messaging/unread-count`).catch(() => null),
      ]);
      let notifs = 0;
      let messages = 0;
      if (nr && nr.ok) {
        const d = await nr.json();
        notifs = Number(d.unread_count || 0);
      }
      if (mr && mr.ok) {
        const d = await mr.json();
        messages = Number(d.total || 0);
      }
      setMessagingUnread(messages);
      setUnread(notifs + messages);

      // Pull recent unseen notifs to find the latest coin_gift. Only
      // queried on the first poll of the session (cheap).
      if (!sessionStorage.getItem('coin_gift_toast_checked')) {
        sessionStorage.setItem('coin_gift_toast_checked', '1');
        const r2 = await authFetch(`${BACKEND_URL}/api/notifications?limit=5`);
        if (!r2.ok) return;
        const data = await r2.json();
        const seenIds = JSON.parse(localStorage.getItem('coin_gift_toast_seen') || '[]');
        const seenSet = new Set(seenIds);
        const fresh = (data.notifications || []).find(
          (n) => n.type === 'coin_gift' && !n.read && !seenSet.has(n.notification_id),
        );
        if (fresh) {
          const amt = fresh.metadata?.amount || 0;
          toast.success(`🎁 +${Number(amt).toLocaleString()} coins gifted!`, {
            description: fresh.body || 'An admin just sent you coins.',
            duration: 6000,
          });
          // Persist so reloading the page doesn't repeat the toast.
          localStorage.setItem(
            'coin_gift_toast_seen',
            JSON.stringify([fresh.notification_id, ...seenIds].slice(0, 20)),
          );
        }
      }
    } catch { /* swallow */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchAll();
  };

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.notification_id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await authFetch(`${BACKEND_URL}/api/notifications/${id}/read`, { method: 'POST' });
    } catch { /* swallow */ }
  };

  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await authFetch(`${BACKEND_URL}/api/notifications/read-all`, { method: 'POST' });
    } catch { /* swallow */ }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-slate-700/60 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center"
        aria-label="Notifications"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5 text-gray-200" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-bell-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-y-auto rounded-xl bg-slate-900 border border-slate-700 shadow-xl z-50"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 sticky top-0 bg-slate-900">
            <span className="text-sm font-semibold text-white">Notifications</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={markAll}
                  className="h-7 px-2 text-[11px] text-pink-300 hover:bg-pink-500/10"
                  data-testid="notifications-mark-all"
                >
                  Mark all read
                </Button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-slate-700"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Inbox CTA — surfaces messages + announcements unread count
              alongside the legacy notifications list. Tap to jump to /inbox. */}
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/inbox'); }}
            className="w-full flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-indigo-500/5 hover:bg-indigo-500/10 transition-colors"
            data-testid="notifications-open-inbox"
          >
            <MessageSquare className="w-4 h-4 text-indigo-300" />
            <span className="text-sm font-semibold text-white">Inbox</span>
            <span className="text-xs text-slate-400 ml-1">messages & announcements</span>
            {messagingUnread > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold">
                {messagingUnread > 99 ? '99+' : messagingUnread}
              </span>
            )}
          </button>

          <div>
            {loading ? (
              <div className="py-6 text-center text-sm text-slate-400">
                <Loader2 className="w-4 h-4 inline-block animate-spin mr-1" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="py-8 px-4 text-center text-sm text-slate-500">
                You have no notifications yet. Tip a writer or unlock a course — the good stuff lands here.
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <li
                    key={n.notification_id}
                    onClick={() => { if (!n.read) markRead(n.notification_id); }}
                    className={`px-3 py-2.5 border-b border-slate-800 last:border-b-0 cursor-pointer hover:bg-slate-800/60 transition-colors ${
                      n.read ? 'opacity-70' : 'bg-pink-500/5'
                    }`}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 shrink-0">{iconFor(n.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{n.title}</div>
                        <div className="text-xs text-slate-300 mt-0.5 line-clamp-3 whitespace-pre-wrap">
                          {n.body}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">{formatTime(n.created_at)}</div>
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-pink-400 mt-1.5 shrink-0" />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
